import { getLocalTimeZone, today } from "@internationalized/date";
import {
  useGetPsychologistIcbtCommunication,
  usePutIcbtRequestFeedbackDate,
} from "api/useIcbtQueries";
import { DatePicker } from "components/datePickers/DatePickerWithSchedule";
import CustomModal from "components/lib/customModal";
import { useSnackbar } from "@mindlercare/ui-web";
import { endOfMonth, startOfMonth } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EditFeedbackDateModalProps {
  intialDate: Date | undefined;
  requestId: string;
  patientUserId: number;
  close: () => void;
}

const EditFeedbackDateModal = ({
  intialDate,
  requestId,
  patientUserId,
  close,
}: EditFeedbackDateModalProps) => {
  const {
    mutateAsync: updateIcbtRequest,
    isPending: isLoading,
    isSuccess,
    isError,
  } = usePutIcbtRequestFeedbackDate();
  const isOpen = Boolean(requestId);
  const timeZone = getLocalTimeZone();
  const todayDate = today(timeZone);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [focusedMonth, setFocusedMonth] = useState(
    startOfMonth(todayDate.toDate(timeZone)),
  );

  const { data, isFetching } = useGetPsychologistIcbtCommunication({
    startDate: focusedMonth,
    endDate: endOfMonth(focusedMonth),
  });

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar({
        text: t("ICBT_FEEDBACK_DATE_UPDATED"),
      });
      close();
    } else if (isError) {
      enqueueSnackbar({
        text: t("ICBT_FEEDBACK_DATE_UPDATE_FAILED"),
        isError: true,
      });
    }
  }, [isSuccess, isError, close, enqueueSnackbar, t]);

  const handleSave = (date: Date | undefined) => {
    if (date) {
      updateIcbtRequest({
        feedbackDate: date,
        icbtRequestId: requestId,
        userId: patientUserId,
      });
    }
  };

  const icbtCommunications = data?.icbtCommunications || [];

  const feedbackDates = icbtCommunications
    .filter(
      ({ request_id: icbtCommunicationRequestId }) =>
        icbtCommunicationRequestId !== requestId,
    )
    .map(({ feedback_date, patient_user_id }) => ({
      date: new Date(feedback_date),
      patientUserId: patient_user_id,
    }));
  const slots = (data?.slots || [])
    .filter((slot) => slot.status === "BOOKED" || slot.status === "OPEN")
    .map((slot) => ({
      date: new Date(slot.startsAt),
      patientUserId: String(slot.patientUserId),
      isBooked: slot.status === "BOOKED",
    }));

  if (!isOpen) return null;

  return (
    <CustomModal.Wrapper
      onClose={close}
      open={isOpen}
      size="large"
      isScrollable={true}
    >
      <DatePicker
        setValue={handleSave}
        value={intialDate}
        isOpen={isOpen}
        feedbackDates={feedbackDates}
        slotDates={slots}
        focusedMonth={focusedMonth}
        setFocusedMonth={setFocusedMonth}
        setIsOpen={() => close()}
        isFetching={isFetching}
        saving={isLoading}
        patientUserId={String(patientUserId)}
      />
    </CustomModal.Wrapper>
  );
};

export default EditFeedbackDateModal;
